import React, { useState, useEffect } from "react";
import { pnpReportData } from "../App";
import Title from "../components/common/Title";
import PNPSalesReport from "../components/sales/PNPSalesReport";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subDays,
  subWeeks,
  subMonths,
  subYears,
} from "date-fns";
import { getNavigatorLanguage } from "../utils";
import notify from "devextreme/ui/notify";
import { alert } from "devextreme/ui/dialog";
import { saveAs } from "file-saver";
import Server from "../api/Server";
import { LoadPanel } from "devextreme-react";
import axios from "axios";

const PNPReport = () => {
  const [state, setState] = useState(pnpReportData);
  const [isExporting, setIsExporting] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isCalling, setIsCalling] = useState(false);

  let ajaxRequest = null;

  useEffect(() => {
    let handle;
    if (isExporting) {
      handle = setInterval(() => exportReport(true), 9000);
    } else {
      if (handle) {
        clearInterval(handle);
      }
    }

    if (isReady) {
      if (handle) {
        clearInterval(handle);
      }
    }

    if (handle) {
      return () => {
        clearInterval(handle);
      };
    }
    // eslint-disable-next-line
  }, [isExporting, isReady]);

  const exportReport = async (secondCall) => {
    const { unixTimeEnd, unixTimeStart, warehouseIds } = state;
    if (!unixTimeEnd || !unixTimeStart || !warehouseIds.length) {
      alert("Start date, End date and Locations are required ", "Notification");
      return;
    }

    // cancel  previous ajax if exists
    if (ajaxRequest && isReady) {
      ajaxRequest.cancel();
      return;
    }

    // creates a new token for upcomming ajax (overwrite the previous one)
    ajaxRequest = axios.CancelToken.source();

    try {
      setIsExporting(true);
      setIsCalling(true);

      let regenerate = true;
      if (secondCall) {
        regenerate = false;
      }

      const params = {
        startDate: `${format(unixTimeStart, "yyyy-MM-dd")}`,
        endDate: `${format(unixTimeEnd, "yyyy-MM-dd")}`,
        regenerate,
      };

      if (warehouseIds.length) {
        params.warehouseIds = warehouseIds.map((e) => e).toString();
      }

      const { data } = await Server.getPNPReportStatus(params, ajaxRequest);

      if (data.data.status === "READY") {
        setIsExporting(false);
        handleDownload(params);
        setIsReady(true);
      }
    } catch (err) {
      if (err.response) {
        const {
          response: { data },
        } = err;
        if (data) {
          notify(`${data.status.errorDescription}`, "error", 3000);
        } else {
          notify("Error exporting report, please try again", "error", 3000);
        }
      } else {
        notify("Error exporting report, please try again", "error", 3000);
      }

      setIsExporting(false);
      setIsCalling(false);
    }
  };

  const handleDownload = async (params) => {
    try {
      setIsCalling(true);
      const { data, headers } = await Server.exportPNPReportToCSV(params);

      if (!data?.data?.status) {
        const blob = new Blob([data], {
          type: headers["content-type"],
        });

        saveAs(blob, `PNP-custom-sales-report-${new Date().getTime()}.csv`);

        notify("Report exported successfully", "success", 3000);
        setIsExporting(false);
        setIsReady(false);
        setIsCalling(false);
      }
    } catch (err) {
      if (err.response) {
        const {
          response: { data },
        } = err;
        if (data) {
          notify(`${data.status.errorDescription}`, "error", 3000);
        } else {
          notify("Error exporting report, please try again", "error", 3000);
        }
      } else {
        notify("Error exporting report, please try again", "error", 3000);
      }

      setIsExporting(false);
      setIsCalling(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    exportReport();
  };

  const onValueChanged = (event, name) => {
    const { value } = event;

    if (name === "dateRange") {
      setQuickDate(value);
    } else {
      setState({ ...state, [name]: value });
    }
  };

  const setQuickDate = (value) => {
    if (value) {
      if (value === "today") {
        setState({
          ...state,
          dateRange: value,
          unixTimeStart: Date.now(),
          unixTimeEnd: Date.now(),
        });
      }

      if (value === "current week") {
        const locale = getNavigatorLanguage();
        const start = startOfWeek(Date.now(), { locale });
        const end = endOfWeek(Date.now(), { locale });

        setState({
          ...state,
          dateRange: value,
          unixTimeStart: start,
          unixTimeEnd: end,
        });
      }

      if (value === "current month") {
        const start = startOfMonth(Date.now());
        const end = endOfMonth(Date.now());

        setState({
          ...state,
          dateRange: value,
          unixTimeStart: start,
          unixTimeEnd: end,
        });
      }

      if (value === "current year") {
        const start = startOfYear(Date.now());
        const end = endOfYear(Date.now());

        setState({
          ...state,
          dateRange: value,
          unixTimeStart: start,
          unixTimeEnd: end,
        });
      }

      if (value === "yesterday") {
        setState({
          ...state,
          dateRange: value,
          unixTimeStart: subDays(Date.now(), 1),
          unixTimeEnd: subDays(Date.now(), 1),
        });
      }

      if (value === "last week") {
        const week = subWeeks(Date.now(), 1);
        const locale = getNavigatorLanguage();
        const start = startOfWeek(week, { locale });
        const end = endOfWeek(week, { locale });
        setState({
          ...state,
          dateRange: value,
          unixTimeStart: start,
          unixTimeEnd: end,
        });
      }

      if (value === "last month") {
        const month = subMonths(Date.now(), 1);
        const start = startOfMonth(month);
        const end = endOfMonth(month);
        setState({
          ...state,
          dateRange: value,
          unixTimeStart: start,
          unixTimeEnd: end,
        });
      }

      if (value === "last year") {
        const year = subYears(Date.now(), 1);
        const start = startOfYear(year);
        const end = endOfYear(year);
        setState({
          ...state,
          dateRange: value,
          unixTimeStart: start,
          unixTimeEnd: end,
        });
      }

      if (value === "last 15 days") {
        setState({
          ...state,
          dateRange: value,
          unixTimeStart: subDays(Date.now(), 14),
          unixTimeEnd: Date.now(),
        });
      }

      if (value === "last 30 days") {
        setState({
          ...state,
          dateRange: value,
          unixTimeStart: subDays(Date.now(), 29),
          unixTimeEnd: Date.now(),
        });
      }

      if (value === "last 60 days") {
        setState({
          ...state,
          dateRange: value,
          unixTimeStart: subDays(Date.now(), 59),
          unixTimeEnd: Date.now(),
        });
      }

      if (value === "last 90 days") {
        setState({
          ...state,
          dateRange: value,
          unixTimeStart: subDays(Date.now(), 89),
          unixTimeEnd: Date.now(),
        });
      }
    } else {
      setState({
        ...state,
        dateRange: value,
        unixTimeStart: null,
        unixTimeEnd: null,
      });
    }
  };

  return (
    <section>
      <Title pageTitle={"PNP custom sales report"} />
      {isCalling && <LoadPanel visible />}
      <PNPSalesReport
        formData={state}
        onValueChanged={onValueChanged}
        handleSubmit={handleSubmit}
        isExporting={isCalling}
      />
    </section>
  );
};

export default PNPReport;
